import { PaymentElement, useElements,useStripe } from '@stripe/react-stripe-js';

import { useEffect, useState } from 'react';

import { Button } from '@ekhodealer/ekho-common/rev';

import type { PaymentIntent } from '@stripe/stripe-js';

import './OnboardingSteps.scss';

interface StripePaymentFormProps {
	/** Callback fired when the payment succeeds or enters processing state */
	onSuccess: (paymentIntent: PaymentIntent) => void;
	/** Optional callback fired when a payment error occurs */
	onError?: (error: { message?: string }) => void;
	/** Label to display on the submit button alongside "Subscribe" */
	priceLabel?: string;
	/** Email address to pre-fill in Stripe billing details */
	prefillEmail?: string;
}

/**
 * Stripe PaymentElement wrapper.
 * Must be rendered inside an <Elements> provider with a clientSecret.
 *
 * Uses a useEffect + onReady pattern (not MutationObserver) to track
 * when the Stripe iframe has finished loading.
 */
export function StripePaymentForm({
	onSuccess,
	onError,
	priceLabel,
	prefillEmail,
}: StripePaymentFormProps) {
	const stripe = useStripe();
	const elements = useElements();
	const [isProcessing, setIsProcessing] = useState(false);
	const [isStripeReady, setIsStripeReady] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	// Reset ready state when the Elements instance changes (e.g. new clientSecret)
	useEffect(() => {
		setIsStripeReady(false);
	}, [elements]);

	const handleReady = () => {
		setIsStripeReady(true);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!stripe || !elements) return;

		setIsProcessing(true);
		setErrorMessage(null);

		const { error, paymentIntent } = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: `${window.location.origin}/onboarding/subscribe`,
			},
			redirect: 'if_required',
		});

		if (error) {
			setErrorMessage(error.message ?? 'Payment failed. Please try again.');
			setIsProcessing(false);
			onError?.(error);
		} else if (paymentIntent) {
			if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
				// 'succeeded' — payment confirmed immediately (most card payments)
				// 'processing' — payment is being processed (some bank transfers)
				// In both cases, treat as success and start polling for backend confirmation
				onSuccess(paymentIntent);
			} else {
				// Unexpected status (requires_action, requires_payment_method, etc.)
				console.error('[StripePaymentForm] Unexpected payment intent status:', paymentIntent.status);
				setErrorMessage(`Unexpected payment status: ${paymentIntent.status}. Please try again.`);
				setIsProcessing(false);
			}
		}
	};

	const buttonText = isProcessing
		? 'Processing...'
		: `Subscribe${priceLabel ? ` — ${priceLabel}` : ''}`;

	return (
		<form onSubmit={handleSubmit} className='onboarding-step__payment-form'>
			{!isStripeReady && (
				<div className='onboarding-step__status'>
					<p>Loading payment form...</p>
				</div>
			)}
			<div style={{ display: isStripeReady ? undefined : 'none' }}>
				<PaymentElement
					onReady={handleReady}
					options={{
						defaultValues: {
							billingDetails: {
								email: prefillEmail,
							},
						},
					}}
				/>
			</div>
			{errorMessage && <div className='onboarding-step__payment-error'>{errorMessage}</div>}
			{isStripeReady && (
				<Button type='submit' variant='filled' isDisabled={!stripe || isProcessing}>
					{buttonText}
				</Button>
			)}
		</form>
	);
}
